import { useEffect, useCallback } from 'react';

export type PanelId = 'email' | 'todo' | 'memory';

export interface KeyboardShortcutsHandlers {
  onExpandPanel: (panel: PanelId) => void;
  onCollapseAll: () => void;
  onOpenCommandPalette: () => void;
  onOpenShortcuts: () => void;
  onCloseModal: () => void;
  // Email panel
  onEmailNav?: (direction: 'up' | 'down') => void;
  onEmailOpen?: () => void;
  onEmailArchive?: () => void;
  onEmailSnooze?: () => void;
  onEmailReply?: () => void;
  onEmailReplyAll?: () => void;
  onEmailForward?: () => void;
  onEmailToggleStar?: () => void;
  onEmailDelete?: () => void;
  onEmailToggleRead?: () => void;
  // Todo panel
  onTodoNav?: (direction: 'up' | 'down') => void;
  onTodoToggle?: () => void;
  onTodoPriority?: (priority: 1 | 2 | 3 | 4) => void;
  // Notes panel
  onNotesNav?: (direction: 'up' | 'down') => void;
  onNotesOpen?: () => void;
  // Compose
  onOpenCompose?: () => void;
  // Signa 2.0 global shortcuts
  onOpenNewNote?: () => void;
  onFocusQuickAdd?: () => void;
}

export function useKeyboardShortcuts(handlers: KeyboardShortcutsHandlers) {
  const {
    onExpandPanel,
    onCollapseAll,
    onOpenCommandPalette,
    onOpenShortcuts,
    onCloseModal,
    onEmailNav,
    onEmailOpen,
    onEmailArchive,
    onEmailSnooze,
    onEmailReply,
    onEmailReplyAll,
    onEmailForward,
    onEmailToggleStar,
    onEmailDelete,
    onEmailToggleRead,
    onTodoNav,
    onTodoToggle,
    onTodoPriority,
    onNotesNav,
    onNotesOpen,
    onOpenCompose,
    onOpenNewNote,
    onFocusQuickAdd,
  } = handlers;

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      const meta = e.metaKey || e.ctrlKey;
      const target = e.target as HTMLElement;
      const isTyping =
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.tagName === 'SELECT' ||
        target.isContentEditable;

      // Escape - close modal or collapse panels
      if (e.key === 'Escape') {
        e.preventDefault();
        onCloseModal();
        return;
      }

      // ? - shortcuts overlay (when not in input)
      if (e.key === '?' && !isTyping) {
        e.preventDefault();
        onOpenShortcuts();
        return;
      }

      // Cmd+K - command palette
      if (meta && e.key === 'k') {
        e.preventDefault();
        onOpenCommandPalette();
        return;
      }

      // C - compose (when not typing)
      if (e.key === 'c' && !isTyping && !meta) {
        e.preventDefault();
        onOpenCompose?.();
        return;
      }

      // N - new note (when not typing)
      if (e.key === 'n' && !isTyping && !meta) {
        e.preventDefault();
        onOpenNewNote?.();
        return;
      }

      // / - focus the Todo quick-add input (when not typing)
      if (e.key === '/' && !isTyping && !meta) {
        e.preventDefault();
        onFocusQuickAdd?.();
        return;
      }

      // Cmd+1/2/3 is handled by useView (view switching). Panel expansion
      // can be invoked by clicking the panel header or via command palette.

      // Email panel shortcuts
      if (onEmailNav || onEmailOpen || onEmailArchive || onEmailSnooze || onEmailReply) {
        if (!isTyping) {
          // j/ArrowDown - next thread
          if (e.key === 'j' || e.key === 'ArrowDown') {
            e.preventDefault();
            onEmailNav?.('down');
          }
          // k/ArrowUp - previous thread
          if (e.key === 'k' || e.key === 'ArrowUp') {
            e.preventDefault();
            onEmailNav?.('up');
          }
          // Enter or o - open selected thread
          if (e.key === 'Enter' || e.key === 'o') {
            e.preventDefault();
            onEmailOpen?.();
          }
          // r - reply
          if (e.key === 'r' && !meta) {
            e.preventDefault();
            onEmailReply?.();
          }
          // a - reply all
          if (e.key === 'a' && !meta) {
            e.preventDefault();
            onEmailReplyAll?.();
          }
          // f - forward
          if (e.key === 'f' && !meta) {
            e.preventDefault();
            onEmailForward?.();
          }
          // e - archive
          if (e.key === 'e') {
            e.preventDefault();
            onEmailArchive?.();
          }
          // s - snooze
          if (e.key === 's' && !meta) {
            e.preventDefault();
            onEmailSnooze?.();
          }
          // x - star/unstar
          if (e.key === 'x') {
            e.preventDefault();
            onEmailToggleStar?.();
          }
          // # - delete
          if (e.key === '#') {
            e.preventDefault();
            onEmailDelete?.();
          }
          // u - mark unread
          if (e.key === 'u' && !meta) {
            e.preventDefault();
            onEmailToggleRead?.();
          }
          // Shift+I - toggle read (mark read/unread)
          if (e.key === 'I' && e.shiftKey) {
            e.preventDefault();
            onEmailToggleRead?.();
          }
        }
      }

      // Todo panel shortcuts
      if (onTodoNav || onTodoToggle || onTodoPriority) {
        if (!isTyping) {
          if (e.key === 'j' || e.key === 'ArrowDown') {
            e.preventDefault();
            onTodoNav?.('down');
          }
          if (e.key === 'k' || e.key === 'ArrowUp') {
            e.preventDefault();
            onTodoNav?.('up');
          }
          if (e.key === ' ') {
            e.preventDefault();
            onTodoToggle?.();
          }
          if (['1', '2', '3', '4'].includes(e.key) && !meta) {
            e.preventDefault();
            onTodoPriority?.(parseInt(e.key) as 1 | 2 | 3 | 4);
          }
        }
      }

      // Notes panel shortcuts
      if (onNotesNav || onNotesOpen) {
        if (!isTyping) {
          if (e.key === 'j' || e.key === 'ArrowDown') {
            e.preventDefault();
            onNotesNav?.('down');
          }
          if (e.key === 'k' || e.key === 'ArrowUp') {
            e.preventDefault();
            onNotesNav?.('up');
          }
          if (e.key === 'Enter') {
            e.preventDefault();
            onNotesOpen?.();
          }
        }
      }
    },
    [
      onExpandPanel,
      onCollapseAll,
      onOpenCommandPalette,
      onOpenShortcuts,
      onCloseModal,
      onEmailNav,
      onEmailOpen,
      onEmailArchive,
      onEmailSnooze,
      onEmailReply,
      onEmailReplyAll,
      onEmailForward,
      onEmailToggleStar,
      onEmailDelete,
      onEmailToggleRead,
      onTodoNav,
      onTodoToggle,
      onTodoPriority,
      onNotesNav,
      onNotesOpen,
      onOpenCompose,
      onOpenNewNote,
      onFocusQuickAdd,
    ]
  );

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);
}
